/**
 * Archived Items — every archived item in the workspace, what happened to it
 * (item_outcomes, mig 860), and "Take back" where the governed reversal will
 * accept it.
 *
 * Take back is the item page's own act (restore_retired_item, mig 752): it
 * covers items retired through retire_new_item, by the retirer, the person
 * who added it, or a workspace admin. It is drawn only where
 * item_outcomes.you_may_restore says that rule holds for this person (§2.8 —
 * no control the person may not use, no refusal after the click). An item
 * closed by a duplicate, merge or supersede decision is reversed through that
 * decision, so it gets the outcome line and no button.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Archive } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { DataTable, EmptyState, TextInput, type Column } from '@/components/ds';
import { ReverseActionButton } from '@/components/reversals/ReverseActionButton';
import { ItemOutcomeLine } from '@/components/prioritization/item-view/ItemOutcomeLine';
import { useItemOutcomes } from '@/hooks/prioritization/useItemOutcomes';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { restoreRetiredItem } from '@/lib/reversals/reversalsService';
import { getItemCountState } from '../../../components/collection/types';

interface ArchivedRow {
  id: string;
  title: string;
  updatedAt: number;
}

export default function ArchivedItems() {
  const items = useItemsStore((s) => s.items);
  const syncState = useItemsStore((s) => s.syncState);
  const fetchFromSupabase = useItemsStore((s) => s.fetchFromSupabase);
  const loadItemById = useItemsStore((s) => s.loadItemById);
  const [query, setQuery] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    void fetchFromSupabase();
  }, [fetchFromSupabase]);

  const archived = useMemo<ArchivedRow[]>(
    () =>
      items
        .filter((i) => (i as { exit_status?: string }).exit_status === 'archived')
        .map((i) => ({
          id: i.id,
          title: i.title,
          updatedAt: i.updatedAt ? new Date(i.updatedAt).getTime() : 0,
        }))
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [items],
  );
  const archivedIds = useMemo(() => archived.map((r) => r.id), [archived]);
  const { outcomes, loading: outcomesLoading } = useItemOutcomes(archivedIds, refreshKey);

  const showSearch = getItemCountState(archived.length) === 'many' || query.length > 0;
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return archived;
    return archived.filter((r) => {
      const o = outcomes.get(r.id);
      return (
        r.title.toLowerCase().includes(q)
        || (o?.pointerItemTitle?.toLowerCase().includes(q) ?? false)
        || (o?.reason?.toLowerCase().includes(q) ?? false)
        || (o?.outcome.includes(q) ?? false)
      );
    });
  }, [archived, outcomes, query]);

  const columns: Column<ArchivedRow>[] = [
    {
      key: 'title',
      header: 'Item',
      width: '34%',
      render: (row) => (
        <Link
          to={`/prioritization/items/${row.id}`}
          className="font-medium text-[var(--text-pri)] hover:underline underline-offset-2"
        >
          {row.title}
        </Link>
      ),
    },
    {
      key: 'outcome',
      header: 'What happened',
      render: (row) => {
        const o = outcomes.get(row.id);
        if (!o) {
          return <span className="text-sm text-[var(--text-ter)]">{outcomesLoading ? 'Reading…' : 'Archived'}</span>;
        }
        return <ItemOutcomeLine outcome={o} />;
      },
    },
    {
      key: 'act',
      header: '',
      width: '9rem',
      align: 'right',
      render: (row) => {
        const o = outcomes.get(row.id);
        if (!o?.youMayRestore) return null;
        return (
          <ReverseActionButton
            label="Take back"
            confirmTitle="Take back this retired item"
            explain="The item returns to the lists as active. Your reason is kept with its history."
            run={(reason) => restoreRetiredItem(row.id, reason)}
            onDone={() => {
              void loadItemById(row.id);
              setRefreshKey((k) => k + 1);
            }}
            variant="ghost"
          />
        );
      },
    },
  ];

  const loading = archived.length === 0 && syncState.isLoading;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Archive className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--surface)]"
        title="Archived Items"
        subtitle={
          archived.length > 0
            ? `${archived.length} item${archived.length === 1 ? '' : 's'} out of active consideration, and what happened to each.`
            : 'Items out of active consideration, and what happened to each.'
        }
      />

      {showSearch && (
        <div className="max-w-sm">
          <TextInput
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search archived items…"
            aria-label="Search archived items"
          />
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        getRowKey={(r) => r.id}
        loading={loading}
        ariaLabel="Archived items"
        empty={
          query ? (
            <EmptyState title="No archived items match your search." />
          ) : (
            <EmptyState
              icon={Archive}
              title="No archived items"
              description="When an item is archived, retired, merged or closed as a duplicate, it is listed here with what happened to it."
              action={
                <Link to="/prioritization/items" className="text-sm font-medium text-[var(--text-pri)] underline underline-offset-2">
                  Go to items
                </Link>
              }
            />
          )
        }
      />
    </div>
  );
}
